// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import Spinner from "@gcg/App/Components/Spinner/Spinner";
import checkQuoteEmail from "@gcg/App/Handlers/checkQuoteEmail";
import { validateRecaptcha } from "@gcg/App/Services/RecaptchaService";
import { RequestSeacoastQuote } from "@gcg/App/Services/RequestQuoteService";
import { linkStyle02WithAnchor } from "@gcg/App/SharedStyles/LinksStyles";
import { CustomAppState, xCustomCartActions } from "@gcg/App/Store/Reducers";
import { doScroll } from "@gcg/App/Utilities/helpers";
import Select from "@gcg/MobiusOverrides/Select";
import TextArea, { TextAreaProps } from "@gcg/MobiusOverrides/TextArea/TextArea";
import TextField, { TextFieldPresentationProps } from "@gcg/MobiusOverrides/TextField";
import CaretDown from "@gcg/XDL/Icons/CaretDown";
import { validateEmail } from "@gcg/XDL/Utilities/inputValidation";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCurrencies,
    getSession,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { CartModel, CountryModel, CurrencyModel, StateModel } from "@insite/client-framework/Types/ApiModels";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridItemProps } from "@insite/mobius/GridItem";
import { GridContainer, GridItem, Hidden, Typography } from "@insite/mobius/index";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useCallback, useContext, useEffect, useRef, useState } from "react";
import { connect, DispatchProp, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import styled, { css } from "styled-components";

/* eslint-disable */
import Reaptcha from "reaptcha";
import { getCurrentCartState, isCartEmpty } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { NewsLetterContext } from "@seacoastusa/App/Pages/CustomCartPage";
/* eslint-enable */

import { getDataLayer } from "@gcg/App/Utilities/DataLayer";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import { setCookie } from "@insite/mobius/utilities/cookies";

interface RequestQuoteFormProps extends WidgetProps {}

interface Data  {
    event: string;
    ecommerce: {
        purchase: {
            actionField: {
                id: string | undefined;
                affiliation: string;
                revenue: number | undefined;
                tax: number | undefined;
                shipping: number | undefined;
            };
            products: GAData[];
        };
    }
}

interface GAData {
    name: string;
    id: string;
    price: number;
    quantity: number;
    category: string;
}

const mapDispatchToProps = {
    loadCurrentCountries,
    checkQuoteEmail,
    setIsSubmiting: (isSubmittingCart: boolean) => (dispatch: Dispatch<xCustomCartActions>) => {
        dispatch({ type: "xCustomCart/isSubmittingCart", isSubmittingCart });
    },
};

const mapStateToProps = (state: CustomAppState & ApplicationState) => {
    const cartState = getCurrentCartState(state);
    const cart = cartState?.value;
    const orderNumber = cartState?.value?.orderNumber;
    return {
        countries: getCurrentCountries(state),
        currencies: getCurrencies(state),
        customSettings: getSettingsCollection(state).customSettings,
        cartConfirmationLink: getPageLinkByPageType(state, "CustomCartConfirmation")?.url || "/CustomCartConfirmation",
        isAuthenticated: getSession(state).isAuthenticated,
        session: getSession(state),
        orderNumber,
        isCartEmpty: isCartEmpty(cart),
        cart
    };
};

type Props = RequestQuoteFormProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory;

export interface CustomCartContactInfoFormWidgetStyles {
    requestQuoteFormWrapper?: InjectableCss;
    formControlWrapperGridItem?: GridItemProps;
    addresslWrapperGridItem?: GridItemProps;
    formControlWrapperFullGridItem?: GridItemProps;
    spacer?: InjectableCss;
    emailAlreadyExists?: InjectableCss;
    title: TypographyProps;
    titleWrapper: GridItemProps;
    placeholderText?: TextFieldPresentationProps;
    textAreaPlaceholder?: TextAreaProps;
}

export const CustomCartContactInfoFormWidgetStyles: CustomCartContactInfoFormWidgetStyles = {
    requestQuoteFormWrapper: {
        css: css`
            position: relative;
        `,
    },
    formControlWrapperGridItem: {
        width: [12, 6, 3, 3, 3],
    },
    addresslWrapperGridItem: {
        width: [12, 6, 6, 6, 6],
    },
    spacer: {
        css: css`
            height: 30px;
        `,
    },
    formControlWrapperFullGridItem: {
        width: 12,
    },
    emailAlreadyExists: {
        css: css`
            margin-bottom: 15px;
            max-width: 100%;

            ${linkStyle02WithAnchor};
        `,
    },
    title: {
        variant: "h2",
        css: css`
            margin-bottom: 0;
        `,
    },
    titleWrapper: {
        width: [12, 6, 6, 9, 9],
    },
    placeholderText: {
        cssOverrides: {
            formField: css`
                input::placeholder {
                    font-style: italic;
                }
            `,
        },
    },
    textAreaPlaceholder: {
        cssOverrides: {
            formField: css`
                textarea::placeholder {
                    font-style: italic;
                }
            `,
        },
    },
};
const Separator = styled.hr`
    width: 100%;
    margin: 20px 0;
`;

const styles = CustomCartContactInfoFormWidgetStyles;

const CustomCartContactInfoForm: FC<Props> = ({
    countries,
    currencies,
    loadCurrentCountries,
    customSettings,
    checkQuoteEmail,
    cartConfirmationLink,
    history,
    isAuthenticated,
    session,
    orderNumber,
    isCartEmpty,
    setIsSubmiting,
    cart,
}) => {
    let error: any = false;
    let RfqCookie;
    let parseCookie = {} as any;
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [attention, setAttention] = useState("");
    const [country, setCountry] = useState<CountryModel | undefined>(undefined);
    const [currency, setCurrency] = useState<CurrencyModel | undefined>(undefined);
    const [address1, setAddress1] = useState("");
    const [address2, setAddress2] = useState("");
    const [city, setCity] = useState("");
    const [states, setStates] = useState<StateModel[]>([]);
    const [state, setState] = useState<StateModel | undefined>(undefined);
    const [postalCode, setPostalCode] = useState("");
    const [notes, setNotes] = useState("");
    const [captchaElm, setCaptchaElm] = useState<any>({} as any);
    const [firstNameError, setFirstNameError] = useState(error);
    const [lastNameError, setLastNameError] = useState(error);
    const [emailError, setEmailError] = useState(error);
    const [phoneError, setPhoneError] = useState(error);
    const [companyNameError, setCompanyNameError] = useState(error);
    const [countryError, setCountryError] = useState(error);
    const [currencyError, setCurrencyError] = useState(error);
    const [address1Error, setAddress1Error] = useState(error);
    const [cityError, setCityError] = useState(error);
    const [stateError, setStateError] = useState(error);
    const [postalCodeError, setPostalCodeError] = useState(error);
    const [userVerified, setUserVerified] = useState(false);
    const [countryId, setCountryId] = useState("");
    const [stateId, setStateId] = useState("");
    const [currencyId, setCurrencyId] = useState("");

    const toasterContext: any = useContext(ToasterContext);
    const { checked: newsLetterCheckbox } = useContext(NewsLetterContext);

    useEffect(() => {
        if (!countries) {
            loadCurrentCountries();
        }
    }, []);

    useEffect(() => {
        if (countries?.length) {
            RfqCookie = getCookie("RFQCookie");
            parseCookie = RfqCookie ? JSON.parse(RfqCookie) : {};
            if (Object.keys(parseCookie).length !== 0) {
                setFirstName(parseCookie?.firstName);
                setLastName(parseCookie?.lastName);
                setEmail(parseCookie?.email);
                setPhone(parseCookie?.phone);
                setCompanyName(parseCookie?.companyName);
                setAttention(parseCookie?.attention);
                setAddress1(parseCookie?.address1);
                setAddress2(parseCookie?.address2);
                setCity(parseCookie?.city);
                setPostalCode(parseCookie?.postalCode);
                setCurrencyId(parseCookie?.currency);
                setCountryId(parseCookie?.country);
                setStateId(parseCookie?.state);
                const currentCountry = countries?.find(country => country.id === parseCookie?.country);
                setCountry(currentCountry);
                setState(currentCountry?.states?.find(state => state.id === parseCookie?.state));
            }
        }
    }, [countries]);

    useEffect(() => {
        setStates(country?.states || []);

        if (country) {
            const selectedCurrency = currencies?.find(c => c.currencyCode === country?.properties.defaultCurrency);

            setCurrency(selectedCurrency);

            return;
        }

        setCurrency(undefined);
    }, [country]);

    useEffect(() => {
        if (userVerified) {
            continueHandlingFormSubmit();
        }
    }, [userVerified]);

    const onRecaptchaVerified = () => {
        setUserVerified(true);
    };

    const forwardRef = useCallback(node => {
        setCaptchaElm(node as any);
    }, []);

    const validateRequiredFields = (fieldName: string, fieldValue: any) => {
        if (!fieldValue || (typeof fieldValue === "string" && !fieldValue.trim())) {
            let errorMessage;

            switch (fieldName) {
                case "firstName":
                    errorMessage = siteMessage("CreateNewAccountInfo_FirstName_Required");
                    setFirstNameError(errorMessage);

                    break;
                case "lastName":
                    errorMessage = siteMessage("CreateNewAccountInfo_LastName_Required");
                    setLastNameError(errorMessage);

                    break;
                case "email":
                    errorMessage = siteMessage("CreateNewAccountInfo_EmailAddress_Required");
                    setEmailError(errorMessage);

                    break;
                case "phone":
                    errorMessage = siteMessage("CreateNewAccountInfo_Phone_Required");
                    setPhoneError(errorMessage);

                    break;
                case "companyName":
                    errorMessage = siteMessage("CreateNewAccountInfo_CompanyName_Required");
                    setCompanyNameError(errorMessage);

                    break;
                case "country":
                    errorMessage = siteMessage("CreateNewAccountInfo_Country_Required");
                    setCountryError(errorMessage);

                    break;
                case "currency":
                    errorMessage = siteMessage("CreateNewAccountInfo_Currency_Required");
                    setCurrencyError(errorMessage);

                    break;
                case "address1":
                    errorMessage = siteMessage("CreateNewAccountInfo_Address1_Required");
                    setAddress1Error(errorMessage);

                    break;
                case "city":
                    errorMessage = siteMessage("CreateNewAccountInfo_City_Required");
                    setCityError(errorMessage);

                    break;
                case "state":
                    errorMessage = siteMessage("CreateNewAccountInfo_State_Required");
                    setStateError(errorMessage);

                    break;
                case "postalCode":
                    errorMessage = siteMessage("CreateNewAccountInfo_PostalCode_Required");
                    setPostalCodeError(errorMessage);

                    break;
            }

            error = true;
        }
    };

    const validateEmailAddress = (email: string) => {
        const errorMessage = validateEmail(email);

        setEmailError(errorMessage);

        if (errorMessage) {
            error = true;
        }
    };

    const handleChange = (fieldName: string, fieldValue: any) => {
        switch (fieldName) {
            case "firstName":
                setFirstNameError("");
                setFirstName(fieldValue);

                break;
            case "lastName":
                setLastNameError("");
                setLastName(fieldValue);

                break;
            case "email":
                setEmailError("");
                setEmail(fieldValue);

                break;
            case "phone":
                setPhoneError("");
                setPhone(fieldValue);

                break;
            case "companyName":
                setCompanyNameError("");
                setCompanyName(fieldValue);

                break;
            case "country":
                setCountryError("");
                setCountry(countries?.find(country => country.id === fieldValue));
                const resultCountry = countries?.find(country => country.id === fieldValue);
                setCountryId(resultCountry?.id as any);

                break;
            case "currency":
                setCurrencyError("");
                setCurrency(currencies?.find(currency => currency.id === fieldValue));
                const resultCurrency = currencies?.find(currency => currency.id === fieldValue);
                setCurrencyId(resultCurrency?.id as any);

                break;
            case "address1":
                setAddress1Error("");
                setAddress1(fieldValue);

                break;
            case "city":
                setCityError("");
                setCity(fieldValue);

                break;
            case "state":
                setStateError("");
                setState(states?.find(state => state.id === fieldValue));
                const resultState = states?.find(state => state.id === fieldValue);
                setStateId(resultState?.id as any);

                break;
            case "postalCode":
                setPostalCodeError("");
                setPostalCode(fieldValue);

                break;
        }
    };

    // xng-change: GCG-658: adding submit RFQ event
    const submitRFQEvent = () =>{
        const dataLayer = getDataLayer();
    
        if (!dataLayer) {
            return;
        }

        const data : Data = {
            event: "QuoteSubmission",
            ecommerce: {
                purchase: {
                    actionField: {
                        id: cart?.orderNumber,
                        affiliation: (cart as CartModel)?.billTo?.companyName || "",
                        revenue: cart?.orderGrandTotal,
                        tax: cart?.totalTax,
                        shipping: cart?.shippingAndHandling,
                    },
                    products: [],
                },
            },
        };

        cart?.cartLines?.forEach((cartLine: any) => {
            const GAData : GAData = {
                name: cartLine.shortDescription,
                id: cartLine.erpNumber,
                price: cartLine.pricing.unitNetPrice,
                quantity: cartLine.qtyOrdered,
                category: cartLine.properties.l1Category
            };

            data.ecommerce?.purchase?.products?.push(GAData);
        });

        dataLayer.push(data);
    }
    

    const handleSubmit = (e: any) => {
        e.preventDefault();

        if (!userVerified) {
            captchaElm.execute();

            return;
        }
        continueHandlingFormSubmit();
    };

    const continueHandlingFormSubmit = () => {
        setIsSubmitted(true);

        if (!isAuthenticated) {
            validateRequiredFields("firstName", firstName);
            validateRequiredFields("lastName", lastName);
            validateRequiredFields("email", email);
            validateEmailAddress(email);
        }
        validateRequiredFields("phone", phone);
        validateRequiredFields("companyName", companyName);
        validateRequiredFields("country", country);
        validateRequiredFields("currency", currency);
        validateRequiredFields("address1", address1);
        validateRequiredFields("city", city);
        validateRequiredFields("state", state);
        validateRequiredFields("postalCode", postalCode);

        if (error) {
            doScroll();

            return;
        }
        setIsSubmiting(true);

        captchaElm.getResponse().then((response: any) => {
            validateRecaptcha(response).then(
                () => {
                    const formData = {} as any;
                    if (!isAuthenticated) {
                        formData.email = email;
                        formData.firstName = firstName;
                        formData.lastName = lastName;
                    } else {
                        formData.email = session.email;
                        formData.firstName = session.firstName;
                        formData.lastName = session.lastName;
                    }
                    formData.phone = phone;
                    formData.companyName = companyName;
                    formData.attention = attention;
                    formData.country = country?.id || "";
                    formData.currency = currency?.id || "";
                    formData.address1 = address1;
                    formData.address2 = address2;
                    formData.city = city;
                    formData.state = state?.id || "";
                    formData.postalCode = postalCode;
                    formData.notes = notes;
                    formData.subscribeToMailChimp = newsLetterCheckbox ? "true" : "false";
                    formData.orderNumber = orderNumber || "";
                    RequestSeacoastQuote(formData).then(
                        response => {
                            toasterContext.addToast({
                                body: siteMessage("Custom_RequestQuote_SuccessMessage"),
                                messageType: "success",
                            });
                            setCookie("RFQCookie", JSON.stringify(formData), { expires: 30 });
                            setIsSubmitting(false);
                            submitRFQEvent();
                            history.push(
                                response.cartId
                                    ? `${cartConfirmationLink}?cartId=${response.cartId}`
                                    : cartConfirmationLink,
                            );
                            setTimeout(() => {
                                setIsSubmiting(false);
                            }, 1000);
                        },
                        () => {
                            setIsSubmitting(false);
                            toasterContext.addToast({
                                body: translate("An error occurred, please try again later."),
                                messageType: "danger",
                            });
                        },
                    );
                },
                () => {
                    captchaElm.execute();
                },
            );
        });
    };

    return (
        <>
            {!isCartEmpty && (
                <StyledWrapper {...styles.requestQuoteFormWrapper}>
                    <form onSubmit={handleSubmit} id="custom-rfq-form" noValidate>
                        <GridContainer>
                            {!isAuthenticated && (
                                <>
                                    <GridItem {...styles.formControlWrapperGridItem}>
                                        <TextField
                                            label={translate("First Name")}
                                            onChange={e => handleChange("firstName", e.currentTarget.value)}
                                            value={firstName}
                                            data-test-selector="requestQuoteForm_firstName"
                                            error={isSubmitted && firstNameError}
                                            maxLength={25}
                                            placeholder={translate("Your First Name")}
                                            {...styles.placeholderText}
                                            required
                                        />
                                    </GridItem>
                                    <GridItem {...styles.formControlWrapperGridItem}>
                                        <TextField
                                            label={translate("Last Name")}
                                            onChange={e => handleChange("lastName", e.currentTarget.value)}
                                            value={lastName}
                                            data-test-selector="requestQuoteForm_lastName"
                                            error={isSubmitted && lastNameError}
                                            maxLength={25}
                                            placeholder={translate("Your Last Name")}
                                            {...styles.placeholderText}
                                            required
                                        />
                                    </GridItem>
                                    <GridItem {...styles.formControlWrapperGridItem}>
                                        <TextField
                                            type="email"
                                            label={translate("Email Address")}
                                            onChange={e => handleChange("email", e.currentTarget.value)}
                                            value={email}
                                            data-test-selector="requestQuoteForm_email"
                                            error={isSubmitted && emailError}
                                            maxLength={256}
                                            minLength={5}
                                            placeholder={translate("Email@Address.com")}
                                            {...styles.placeholderText}
                                            required
                                        />
                                    </GridItem>
                                </>
                            )}
                            <GridItem {...styles.formControlWrapperGridItem}>
                                <TextField
                                    type="tel"
                                    label={translate("Phone")}
                                    onChange={e => handleChange("phone", e.currentTarget.value)}
                                    value={phone}
                                    data-test-selector="requestQuoteForm_phone"
                                    error={isSubmitted && phoneError}
                                    placeholder={translate("( ___ ) ___-____")}
                                    required
                                />
                            </GridItem>
                        </GridContainer>

                        <StyledWrapper {...styles.spacer} />
                        <GridContainer>
                            <GridItem {...styles.formControlWrapperGridItem}>
                                <TextField
                                    label={translate("Company Name")}
                                    onChange={e => handleChange("companyName", e.currentTarget.value)}
                                    value={companyName}
                                    data-test-selector="requestQuoteForm_companyName"
                                    error={isSubmitted && companyNameError}
                                    maxLength={100}
                                    placeholder={translate("Company's Name")}
                                    {...styles.placeholderText}
                                    required
                                />
                            </GridItem>
                            <GridItem {...styles.formControlWrapperGridItem}>
                                <TextField
                                    label={translate("Attention (Optional)")}
                                    onChange={e => setAttention(e.currentTarget.value)}
                                    value={attention}
                                    data-test-selector="requestQuoteForm_companyName"
                                    maxLength={100}
                                    placeholder={translate("Who is this For?")}
                                    {...styles.placeholderText}
                                    required
                                />
                            </GridItem>
                            {countries && countries?.length && (
                                <GridItem {...styles.formControlWrapperGridItem}>
                                    <Select
                                        name="country"
                                        label={translate("Country")}
                                        //   value={country?.id || ""}
                                        value={countryId || ""}
                                        onChange={e => handleChange("country", e.target.value)}
                                        error={isSubmitted && countryError}
                                        data-test-selector="requestQuoteForm_country"
                                        iconProps={{ src: CaretDown, color: "common.ui1" }}
                                        required
                                    >
                                        <option value="">{translate("Select Country")}</option>
                                        {countries.map(country => (
                                            <option key={country.id} value={country.id}>
                                                {country.name}
                                            </option>
                                        ))}
                                    </Select>
                                </GridItem>
                            )}
                            {currencies && currencies?.length && (
                                <GridItem {...styles.formControlWrapperGridItem}>
                                    <Select
                                        label={translate("Currency")}
                                        value={currencyId || ""}
                                        onChange={e => handleChange("currency", e.target.value)}
                                        error={isSubmitted && currencyError}
                                        data-test-selector="requestQuoteForm_currency"
                                        iconProps={{ src: CaretDown, color: "common.ui1" }}
                                        required
                                    >
                                        <option value="">{translate("Select Currency")}</option>
                                        {currencies.map(currency => (
                                            <option value={currency.id} key={currency.id}>
                                                {currency.currencyCode}
                                            </option>
                                        ))}
                                    </Select>
                                </GridItem>
                            )}
                        </GridContainer>
                        <GridContainer>
                            <GridItem {...styles.formControlWrapperFullGridItem}>
                                <Separator />
                            </GridItem>
                        </GridContainer>
                        <GridContainer>
                            <GridItem {...styles.addresslWrapperGridItem}>
                                <TextField
                                    label={translate("Address 1")}
                                    name="address1"
                                    value={address1}
                                    maxLength={100}
                                    onChange={e => handleChange("address1", e.currentTarget.value)}
                                    error={isSubmitted && address1Error}
                                    data-test-selector="requestQuoteForm_address1"
                                    placeholder={translate("Shipping Address Line 1")}
                                    {...styles.placeholderText}
                                    required
                                />
                            </GridItem>
                            <GridItem {...styles.addresslWrapperGridItem}>
                                <TextField
                                    name="address2"
                                    label={translate("Address 2 (Optional)")}
                                    value={address2}
                                    maxLength={100}
                                    onChange={e => setAddress2(e.currentTarget.value)}
                                    data-test-selector="requestQuoteForm_address1"
                                    placeholder={translate("Shipping Address Line 2")}
                                    {...styles.placeholderText}
                                />
                            </GridItem>
                        </GridContainer>
                        <GridContainer>
                            <GridItem {...styles.formControlWrapperGridItem}>
                                <TextField
                                    name="city"
                                    label={translate("Town/City")}
                                    onChange={e => handleChange("city", e.currentTarget.value)}
                                    value={city}
                                    data-test-selector="requestQuoteForm_city"
                                    error={isSubmitted && cityError}
                                    maxLength={100}
                                    placeholder={translate("City Name")}
                                    {...styles.placeholderText}
                                    required
                                />
                            </GridItem>
                            {countries && countries?.length && (
                                <GridItem {...styles.formControlWrapperGridItem}>
                                    <Select
                                        name="state"
                                        label={translate("State")}
                                        value={stateId || ""}
                                        disabled={!states.length}
                                        onChange={e => handleChange("state", e.target.value)}
                                        error={isSubmitted && stateError}
                                        data-test-selector="requestQuoteForm_state"
                                        iconProps={{ src: CaretDown, color: "common.ui1" }}
                                        required
                                    >
                                        <option value="">{translate("Select State")}</option>
                                        {states.map(state => (
                                            <option key={state.id} value={state.id}>
                                                {state.name}
                                            </option>
                                        ))}
                                    </Select>
                                </GridItem>
                            )}
                            <GridItem {...styles.formControlWrapperGridItem}>
                                <TextField
                                    name="postalCode"
                                    label={translate("ZIP / Postal Code")}
                                    onChange={e => handleChange("postalCode", e.currentTarget.value)}
                                    value={postalCode}
                                    data-test-selector="requestQuoteForm_postalCode"
                                    error={isSubmitted && postalCodeError}
                                    maxLength={50}
                                    placeholder={translate("Zip / Postal Code")}
                                    {...styles.placeholderText}
                                    required
                                />
                            </GridItem>
                            <StyledWrapper {...styles.spacer} />
                        </GridContainer>
                        <GridContainer>
                            <GridItem {...styles.formControlWrapperFullGridItem}>
                                <Separator />
                            </GridItem>
                        </GridContainer>
                        <GridContainer>
                            <GridItem {...styles.formControlWrapperFullGridItem}>
                                <TextArea
                                    name="notes"
                                    label={translate("Comments (Optional)")}
                                    onChange={e => setNotes(e.currentTarget.value)}
                                    value={notes}
                                    data-test-selector="requestQuoteForm_postalCode"
                                    maxLength={300}
                                    placeholder={translate(
                                        "Please add any additional comments or questions you may have for this RFQ...",
                                    )}
                                    {...styles.textAreaPlaceholder}
                                />
                            </GridItem>
                        </GridContainer>
                        <StyledWrapper {...styles.spacer} />
                        <Reaptcha
                            ref={forwardRef}
                            sitekey={(customSettings as any).reCaptchaSitekey}
                            size="invisible"
                            onVerify={onRecaptchaVerified}
                            onExpire={() => {
                                setUserVerified(false);
                            }}
                        />
                        {isSubmitting && <Spinner />}
                    </form>
                </StyledWrapper>
            )}
        </>
    );
};

export const definition: WidgetDefinition = {
    group: "Basic",
    fieldDefinitions: [],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CustomCartContactInfoForm)),
    definition,
};

export default widgetModule;
